import { getTranslation } from '../../utils/translation.utils';
import type { LocationRecord } from './apiTypes';
import type { PngCard } from './qr';

/** Text printed on the cards. It is read by guests, so it follows the guest languages, not the panel language. */
export const PRINT_TEXT: Record<string, { scan: string; table: string; togo: string }> = {
    en: { scan: 'Scan to order', table: 'Table', togo: 'Order here - to go' },
    pl: {
        scan: 'Zeskanuj, aby zamówić',
        table: 'Stolik',
        togo: 'Zamów tutaj - na wynos',
    },
    es: {
        scan: 'Escanea para pedir',
        table: 'Mesa',
        togo: 'Pide aquí - para llevar',
    },
    it: {
        scan: 'Inquadra per ordinare',
        table: 'Tavolo',
        togo: 'Ordina qui - da asporto',
    },
    ja: {
        scan: 'スキャンして注文',
        table: 'テーブル',
        togo: 'こちらで注文 - お持ち帰り',
    },
};

/** The label as printed: a plain number reads "Table 12"; any other label ("Terrace 4", "Bar") is printed as it is. */
export const cardLabel = (label: string, lang: string): string => (/^\d+$/.test(label) ? `${(PRINT_TEXT[lang] ?? PRINT_TEXT.en).table} ${label}` : label);

export const PRINT_FORMATS = ['a4-6', 'a6-1'] as const;
export type PrintFormat = (typeof PRINT_FORMATS)[number];

/** Cards per sheet and QR size in mm. */
export const FORMAT_SPEC: Record<PrintFormat, { perPage: number; qrMm: number; size: string; margin: string }> = {
    'a4-6': { perPage: 6, qrMm: 48, size: 'A4 portrait', margin: '10mm' },
    'a6-1': { perPage: 1, qrMm: 70, size: '105mm 148mm', margin: '5mm' },
};

/** The to-go / counter code is one large card on its own A4 page. */
export const TOGO_SPEC = {
    qrMm: 120,
    size: 'A4 portrait',
    margin: '10mm',
} as const;

/** What the PNG download draws around a code, in the venue's first printable language, like the first card of the print view. */
export const pngCard = (location: LocationRecord, caption: (lang: string) => string | undefined): PngCard => {
    const { restaurant } = location;
    const lang = restaurant.supportedLanguages.find((l) => l in PRINT_TEXT) ?? 'en';
    const text = caption(lang);
    return {
        venue: getTranslation(restaurant.translations, 'name', restaurant.name, lang),
        logoUrl: restaurant.logoUrl || null,
        ...(text ? { caption: text } : {}),
    };
};
